import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Controller, useForm } from 'react-hook-form';
import { Divider } from 'react-native-elements';
import { Text } from '@/components/Text';
import {
  addPatient,
  fetchPatient,
  updatePatient,
  updatePatientSettings,
} from '@/api/services/patient';
import { fetchPatientsList } from '@/api/services/user';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import {
  ColorOption,
  ColorPicker,
  DEFAULT_PROFILE_COLOR,
} from '@/components/ColorPicker';
import { Input } from '@/components/ui/therapist/Input';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import { Card } from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { Links } from '@/config/links';
import { openLink } from '@/utils/linkHelpers';
import colors from '@/config/colors';
import { getInitials } from '@/utils/stringHelpers';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { useSubmitState } from '@/hooks/useSubmitState';
import { logError } from '@/utils/logHandling';

import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { validatePatientCode } from '@/utils/stringValidator';
import { styles } from './styles';
import {
  TagSelect,
  tagListMaxHeight,
} from '@/components/ui/therapist/TagSelect';
import {
  createTherapistTags,
  fetchTherapistTags,
} from '@/api/services/therapistTags';
import { Modal } from '@/components/ui/therapist/Modal';
import { TherapistTag } from '@/stores/store.types';

type AddPatientFormData = {
  firstName: string;
  lastName: string;
  profileColor: ColorOption | '';
  biography: string;
  gender: string;
  diagnosis: string;
  code: string;
};

const maxInputWidth = 400;
const maxTherapistTags = 3;

export default function AddPatient({
  route,
  navigation,
}: StackScreenProps<TherapistStackParamList, 'AddPatientScreen'>) {
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<AddPatientFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      profileColor: DEFAULT_PROFILE_COLOR,
      biography: '',
      gender: '',
      diagnosis: '',
      code: '',
    },
  });
  const { showNotification } = useNotification();
  const profileColor = watch('profileColor');
  const firstName = watch('firstName');
  const lastName = watch('lastName');

  const [showModalToggle, setShowModalToggle] = useState(false);
  const [showMaxTherapistTagsModal, setShowMaxTherapistTagsModal] =
    useState(false);
  const patientId = route?.params?._id;

  const [therapistTagOptions, setTherapistTagOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedTherapistTags, setSelectedTherapistTags] = useState<string[]>(
    [],
  );
  const [newTherapistTags, setNewTherapistTags] = useState<string[]>([]);

  useEffect(() => {
    const loadTherapistTags = async () => {
      try {
        const tags = await fetchTherapistTags();

        setTherapistTagOptions(
          tags.map((tag) => ({
            label: tag.name,
            value: tag._id,
          })),
        );
      } catch (error) {
        logError('Could not fetch therapist tags', error);
      }
    };

    loadTherapistTags();
  }, []);

  const handleAddTherapistTag = (label: string) => {
    const trimmedLabel = label.trim();

    setTherapistTagOptions((prev) => [
      ...prev,
      {
        label: trimmedLabel,
        value: trimmedLabel,
      },
    ]);

    setNewTherapistTags((prev) => [...prev, trimmedLabel]);
    setSelectedTherapistTags((prev) => [...prev, trimmedLabel]);
  };

  const resolveSelectedTherapistTagIds = async (): Promise<string[]> => {
    const selectedNewTagNames = newTherapistTags.filter((name) =>
      selectedTherapistTags.includes(name),
    );

    const existingTagIds = selectedTherapistTags.filter(
      (value) => !newTherapistTags.includes(value),
    );

    if (selectedNewTagNames.length === 0) {
      return existingTagIds;
    }

    const tags = await createTherapistTags(selectedNewTagNames);

    const resolvedTags = selectedNewTagNames.map((name) => {
      const tag = tags.find(
        (tag) => tag.name.trim().toLowerCase() === name.trim().toLowerCase(),
      );

      if (!tag) {
        throw new Error(`Could not resolve therapist tag "${name}"`);
      }

      return {
        name,
        id: tag._id,
      };
    });

    const tagIdByName = new Map(resolvedTags.map(({ name, id }) => [name, id]));

    setSelectedTherapistTags((prev) =>
      prev.map((value) => tagIdByName.get(value) ?? value),
    );

    setTherapistTagOptions((prev) =>
      prev.map((option) => ({
        ...option,
        value: tagIdByName.get(option.value) ?? option.value,
      })),
    );

    setNewTherapistTags((prev) =>
      prev.filter((name) => !tagIdByName.has(name)),
    );

    return [...existingTagIds, ...resolvedTags.map(({ id }) => id)];
  };

  const onSubmit = async (data: AddPatientFormData) => {
    try {
      let p;
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        const therapistTags = await resolveSelectedTherapistTagIds();
        if (patientId) {
          p = await updatePatient(
            {
              biography: data.biography,
              diagnosis: data.diagnosis,
              firstName: data.firstName,
              gender: data.gender,
              profileColor: data.profileColor,
              code: data.code,
              lastName: data.lastName,
              therapistTags,
              _id: patientId,
            },
            patientId,
          );
          const codeValid = await updatePatientSettings(
            { code: data.code },
            patientId,
          );
          if (data.code && !codeValid) {
            showNotification({
              type: 'error',
              title: 'Patient:innencode konnte nicht gespeichert werden.',
            });
          }
        } else {
          const { code, ...createData } = data;
          p = await addPatient({
            ...createData,
            code,
            therapistTags,
          });
          if (code) {
            const codeValid = await updatePatientSettings({ code }, p.data._id);
            if (!codeValid) {
              showNotification({
                type: 'error',
                title: 'Patient:innencode konnte nicht gespeichert werden.',
              });
            }
          }
        }
        const patients = await fetchPatientsList();
        showNotification({
          type: 'success',
          title: 'Profil wurde erfolgreich gespeichert',
        });
        navigation.navigate('PatientProfile', {
          id: p.data._id,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients: undefined,
            patientId: p.data._id,
            patient: p.data,
          }),
        });
      } else {
        setShowModalToggle(true);
      }
    } catch (error: any) {
      showNotification({
        type: 'error',
        title:
          error.response?.data?.message ||
          'Patient:innen konnte nicht gespeichert werden.',
      });
      logError('Could not update patient', error);
    }
  };

  const { isSubmitting, submit } = useSubmitState({
    onSubmit: handleSubmit(onSubmit),
  });

  useEffect(() => {
    const findPatient = async () => {
      if (patientId) {
        try {
          const patient = await fetchPatient(patientId);
          let {
            firstName,
            lastName,
            gender,
            diagnosis,
            biography,
            profileColor,
            code,
            therapistTags,
          } = patient;

          setValue('firstName', firstName);
          setValue('lastName', lastName);
          setValue('gender', gender);
          setValue('biography', biography);
          setValue('diagnosis', diagnosis);
          setValue('profileColor', profileColor || DEFAULT_PROFILE_COLOR);
          setValue('code', code ?? '');
          setSelectedTherapistTags(
            therapistTags?.map((tag: TherapistTag) => tag._id) ?? [],
          );

          trigger();
        } catch (error) {
          logError('Could not fetch patient', error);
        }
      }
    };
    findPatient();
  }, []);

  useEffect(() => {
    navigation.setOptions({
      title: patientId ? 'Profil bearbeiten' : 'Profil hinzufügen',
      headerRight: () => (
        <Button label="Abbrechen" icon="close" onPress={navigation.goBack} />
      ),
    });
  }, []);

  return (
    <BodyWrapper
      scrollEnabled={false}
      showSidebar={false}
      bodyStyle={{ width: '100%' }}
    >
      <KeyboardAwareScrollView
        enableOnAndroid
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        extraHeight={tagListMaxHeight}
        extraScrollHeight={30}
        contentContainerStyle={{ paddingBottom: 200 }}
      >
        <Card styleBody={{ padding: 0 }} style={styles.cardStyle}>
          <View style={styles.section}>
            <View>
              <Text style={styles.fieldLabel}>Patient:in Profilname</Text>
              <Text style={styles.fieldInfo}>
                Bei Verwendung des Klarnamens sollte Ihnen eine{' '}
                <Text
                  onPress={() => openLink(Links.confidentiality.href)}
                  style={styles.link}
                >
                  Schweigepflichtsentbindung
                </Text>{' '}
                vorliegen.
              </Text>
            </View>
            <View style={styles.nameRow}>
              <View style={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="firstName"
                  rules={{
                    required: 'Bitte geben Sie den Vornamen an',
                  }}
                  render={({ field }) => (
                    <Input
                      testID="patient-profile-first-name-input"
                      label="Vorname"
                      autoCapitalize="words"
                      required={true}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        trigger('firstName');
                      }}
                      status={
                        errors.firstName
                          ? 'warning'
                          : field.value.length > 0
                            ? 'validated'
                            : 'default'
                      }
                      statusMessage={errors.firstName?.message}
                    />
                  )}
                />
              </View>
              <View style={{ flex: 1 }}>
                <Controller
                  control={control}
                  name="lastName"
                  rules={{
                    required: 'Bitte geben Sie den Nachnamen an',
                  }}
                  render={({ field }) => (
                    <Input
                      testID="patient-profile-last-name-input"
                      label="Nachname"
                      autoCapitalize="words"
                      required={true}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        trigger('lastName');
                      }}
                      status={
                        errors.lastName
                          ? 'warning'
                          : field.value.length > 0
                            ? 'validated'
                            : 'default'
                      }
                      statusMessage={errors.lastName?.message}
                    />
                  )}
                />
              </View>
            </View>
          </View>
          <Divider color={colors.newUI.therapist.grey20} width={1} />
          <View style={styles.section}>
            <ColorPicker
              onColorPress={(color: ColorOption) =>
                setValue('profileColor', color, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              profileColor={profileColor}
              patientInitials={getInitials(firstName + ' ' + lastName)}
            />
          </View>
        </Card>
        <Card styleBody={styles.section} style={styles.cardStyle}>
          <View>
            <Text style={styles.fieldLabel}>Patienten-Code</Text>
            <Text style={styles.fieldInfo}>
              Der Patienten-Code wird für die Zuweisung von Eigentrainings
              benötigt. Sie können den Code später nachtragen.
            </Text>
            <Controller
              control={control}
              name="code"
              rules={{
                validate: (v) => !v || validatePatientCode(v),
              }}
              render={({ field }) => (
                <Input
                  testID="patient-profile-code-input"
                  style={{ maxWidth: maxInputWidth }}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v);
                    trigger('code');
                  }}
                  onBlur={field.onBlur}
                  status={
                    errors.code
                      ? 'warning'
                      : field.value.length > 0
                        ? 'validated'
                        : 'default'
                  }
                  statusMessage={
                    errors.code ? 'Ungültiger Patient:innencode' : undefined
                  }
                />
              )}
            />
          </View>
          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Input
                  testID="patient-profile-gender-input"
                  label="Geschlecht"
                  style={{ maxWidth: maxInputWidth }}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    trigger('gender');
                  }}
                  status={
                    errors.gender
                      ? 'warning'
                      : field.value
                        ? 'validated'
                        : undefined
                  }
                />
              )}
            />
          </View>

          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="diagnosis"
              render={({ field }) => (
                <Input
                  testID="patient-profile-diagnosis-input"
                  label="Diagnose"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  status={field.value.length > 0 ? 'validated' : 'default'}
                />
              )}
            />
          </View>
          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="biography"
              render={({ field }) => {
                const charCount = field.value.length;
                return (
                  <Input
                    testID="patient-profile-note-input"
                    label="Notizen"
                    placeholder="(optional, max. 150 Zeichen)"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    status={charCount > 0 ? 'validated' : 'default'}
                    additionalInfo={`${charCount}/150`}
                    multiline
                    numberOfLines={3}
                    maxLength={150}
                  />
                );
              }}
            />
          </View>
        </Card>
        <Card styleBody={styles.section} style={styles.cardStyle}>
          <View>
            <Text style={styles.fieldLabel}>Filterbegriffe hinzufügen</Text>

            <Text style={styles.fieldInfo}>
              Fügen Sie zum Beispiel behandelnde Therapeut:innen oder Stationen
              hinzu, um einfacher filtern zu können.
            </Text>

            <TagSelect
              testID="patient-profile-info-tags-dropdown"
              options={therapistTagOptions}
              value={selectedTherapistTags}
              onChange={setSelectedTherapistTags}
              onMaxSelectedAttempt={() => setShowMaxTherapistTagsModal(true)}
              onAddTag={handleAddTherapistTag}
              maxSelected={maxTherapistTags}
              placeholder="Filterbegriff hinzufügen"
              style={{ maxWidth: maxInputWidth }}
            />
          </View>
        </Card>
        <View style={styles.footer}>
          <View style={styles.footerButtons}>
            <Button
              look="tertiary"
              onPress={() => navigation.goBack()}
              label="Abbrechen"
              disabled={isSubmitting}
            />
            <Button
              testID="patient-profile-submit-button"
              icon={patientId ? undefined : 'addUser'}
              look="primary"
              onPress={submit}
              label={patientId ? 'Speichern' : 'Patient:in hinzufügen'}
              disabled={!isValid || isSubmitting}
            />
          </View>
        </View>
      </KeyboardAwareScrollView>
      <Modal
        title="Keine Verbindung zum neolexon Server"
        isVisible={showModalToggle}
        onClose={() => setShowModalToggle(false)}
        confirmActionButton={
          <Button
            look="primary"
            label="OK"
            onPress={() => setShowModalToggle(false)}
          />
        }
      >
        <Text>
          Bitte überprüfen Sie Ihre Internetverbindung und versuchen es erneut.
        </Text>
      </Modal>
      <Modal
        title="Limit erreicht"
        isVisible={showMaxTherapistTagsModal}
        onClose={() => setShowMaxTherapistTagsModal(false)}
        confirmActionButton={
          <Button
            look="primary"
            label="OK"
            onPress={() => setShowMaxTherapistTagsModal(false)}
          />
        }
      >
        <Text>
          Es können maximal drei Patient:innen Filterbegriffe pro Patient:in
          hinzugefügt werden.
        </Text>
      </Modal>
    </BodyWrapper>
  );
}
